import { FC, ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IPaginatedResult } from 'src/api/paginated-result'
import { CenteredSpinner } from 'src/components/Spinner'
import TabbedContainer, { TabConfigurationProps } from 'src/components/tabs/GenericTabbedContainer'
import { APIResultCount, TableActionTypes } from 'src/constant'
import { ComponentPlacementType } from 'src/enums'
import { IPositionDrawing, IPositionDrawingWithFile } from 'src/generated-types/position-drawing'
import { useDialogCtx, useMooringsCtx } from 'src/hooks/context-hooks'
import { useGetComponents } from '../../../api/components'
import { IComponent, IPositionWithDrawing } from '../../../generated-types'
import { UpdateComponentDialog } from '../positionsComponents/UpdateComponentDialog'
import { ComponentsTable } from './Components/ComponentsTable'
import PositionsTableTemplateRender from './Helpers/PositionsTableHelper'

export interface PositionInformationContainerProps {
  customerId: EntityId
  positionId: EntityId
  localityId: EntityId
  mooringId: EntityId
  position: IPositionWithDrawing
  availableDrawings?: IPaginatedResult<IPositionDrawing> | undefined
  isLoadingTemplates?: boolean
}

const PositionInformationContainer: FC<PositionInformationContainerProps> = ({
  customerId,
  positionId,
  localityId,
  mooringId,
  position,
  availableDrawings,
  isLoadingTemplates,
}) => {
  const [componentsList, setComponentsList] = useState([] as IComponent[])
  const [componentPlacementTabType, setComponentPlacementTabType] = useState(ComponentPlacementType.Lifted)
  const { data: components, isLoading } = useGetComponents(
    customerId as EntityId,
    positionId as EntityId,
    0,
    APIResultCount.Max,
  )
  const { setSelectedPositionDrawing } = useMooringsCtx()

  useEffect(() => {
    if (componentPlacementTabType == ComponentPlacementType.Sunk)
      setSelectedPositionDrawing && setSelectedPositionDrawing(position.sunkPositionDrawing as IPositionDrawingWithFile)
    else setSelectedPositionDrawing && setSelectedPositionDrawing(position.positionDrawing as IPositionDrawingWithFile)
  }, [componentPlacementTabType, position])

  const { showDialog } = useDialogCtx()
  const { t } = useTranslation(['position'])

  useEffect(() => {
    setComponentsList((components?.items as IComponent[]) ?? [])
  }, [components])

  const onEdit = async (id: EntityId) => {
    const updated = await showDialog(
      UpdateComponentDialog,
      {
        componentProps: {
          customerId,
          localityId,
          mooringId,
          positionId,
          id,
        },
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
      },
      undefined,
      true,
    )
    if (!updated) return
  }

  const sharedTableProps = useMemo(
    () => ({
      onEdit: onEdit,
      setComponents: setComponentsList,
      customerId: customerId as EntityId,
      positionId: positionId as EntityId,
      localityId: localityId as EntityId,
      mooringId: mooringId as EntityId,
      actionType: TableActionTypes.KebabMenu,
    }),
    [onEdit, componentsList],
  )
  const tabConfigurations: TabConfigurationProps[] = useMemo(
    () => [
      {
        id: ComponentPlacementType.Lifted,
        title: t('lifted', { ns: 'main-component' }),
        itemCount: 0,
        shouldShowBadge: false,
        componentProps: {
          components: componentsList.filter(c => c.placement == ComponentPlacementType.Lifted),
          ...sharedTableProps,
        },
      },
      {
        id: ComponentPlacementType.Sunk,
        title: t('sunk', { ns: 'main-component' }),
        itemCount: 0,
        shouldShowBadge: false,
        componentProps: {
          components: componentsList.filter(c => c.placement == ComponentPlacementType.Sunk),
          ...sharedTableProps,
        },
      },
    ],
    [componentsList, t],
  )
  const topActions: ReactNode[] = useMemo(
    () => [
      <PositionsTableTemplateRender
        key={'add-drawing'}
        position={position}
        customerId={customerId}
        localityId={localityId}
        mooringId={mooringId as EntityId}
        availablePositionDrawings={availableDrawings}
        isLoading={isLoadingTemplates}
        componentPlacementTabType={componentPlacementTabType}
      />,
    ],
    [availableDrawings, componentPlacementTabType],
  )

  if (isLoading) return <CenteredSpinner containerProps={{ minHeight: 'calc(100vh * 0.3)' }} />

  return (
    <>
      <TabbedContainer
        tabConfigs={tabConfigurations}
        ContentComponent={ComponentsTable}
        shouldShowTabBar={position.isMultiPlacementsEnabled}
        headerActions={position.isMultiPlacementsEnabled ? topActions : undefined}
        onTabChange={setComponentPlacementTabType}
        currentActiveTab={componentPlacementTabType}
      />
    </>
  )
}

export default PositionInformationContainer
